import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { EnvelopeSimple } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";

const BTN = "border border-black px-3 py-1.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-40";

function errText(e, fallback) {
  const d = e.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

function LastRun({ run }) {
  if (!run) return <span>No summary has been sent yet.</span>;
  if (run.status === "disabled") return <span>Last run {new Date(run.ran_at).toLocaleString()} — skipped, summary was off.</span>;
  return (
    <span>
      Last run {new Date(run.ran_at).toLocaleString()} ({run.trigger}) for {run.week_start} – {run.week_end}:{" "}
      {run.sent} sent, {run.skipped} skipped (no activity), {run.already_sent} already sent, {run.failed} failed.
    </span>
  );
}

function BuyerToggles({ buyers, masterOn, onToggle, busy, canEdit }) {
  if (!buyers.length) {
    return <div className="label-mono text-zinc-500" data-testid="buyer-weekly-summary-empty">No buyers with a funnel profile yet.</div>;
  }
  return (
    <div className="border border-black bg-white divide-y divide-zinc-200">
      {buyers.map((b) => (
        <div key={b.page_id} className="flex items-center gap-3 px-3 py-2" data-testid={`buyer-weekly-summary-row-${b.code}`}>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold truncate">{b.firm_name}</div>
            <div className="text-[10px] text-zinc-500 font-mono truncate">
              {b.recipients.length ? b.recipients.join(", ") : "No buyer-portal users linked — nothing to send"}
            </div>
          </div>
          <Switch checked={b.enabled} disabled={!canEdit || busy === b.page_id} onCheckedChange={(v) => onToggle(b, v)}
                  className={masterOn ? "" : "opacity-60"} data-testid={`buyer-weekly-summary-toggle-${b.code}`} />
        </div>
      ))}
    </div>
  );
}

function PreviewTable({ preview }) {
  const th = "text-left px-3 py-2 text-[10px] uppercase tracking-widest";
  return (
    <div className="overflow-x-auto border border-black bg-white" data-testid="buyer-weekly-summary-preview">
      <div className="label-mono px-3 py-2 border-b border-black">Dry run for {preview.week_start} – {preview.week_end} (nothing sent)</div>
      <table className="w-full text-xs min-w-[560px]">
        <thead className="bg-zinc-100"><tr>
          <th className={th}>Recipient</th><th className={th}>Buyer</th><th className={th}>Visits</th>
          <th className={th}>Leads</th><th className={th}>Delivered</th><th className={th}>Outcome</th>
        </tr></thead>
        <tbody>
          {preview.recipients.map((r) => (
            <tr key={r.user_id} className="border-t border-zinc-200" data-testid={`buyer-weekly-summary-preview-row-${r.user_id}`}>
              <td className="px-3 py-2 font-mono text-[11px]">{r.email}</td>
              <td className="px-3 py-2">{r.firms.join(", ")}</td>
              <td className="px-3 py-2 font-mono">{r.totals.visits}</td>
              <td className="px-3 py-2 font-mono">{r.totals.leads}</td>
              <td className="px-3 py-2 font-mono">{r.totals.delivered}</td>
              <td className="px-3 py-2">{r.already_sent ? "Already sent this week" : (r.will_send ? "Will send" : r.skip_reason)}</td>
            </tr>
          ))}
          {!preview.recipients.length && (
            <tr><td colSpan={6} className="px-3 py-4 text-center label-mono">No buyer would get an email.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export const BuyerWeeklySummaryPanel = () => {
  const { user } = useAuth();
  const canEdit = ["super_admin", "admin"].includes(user?.role);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const r = await api.get("/admin/buyer-weekly-summary");
    setData(r.data);
    setLoadError("");
  }, []);
  useEffect(() => { load().catch((e) => setLoadError(errText(e, "Could not load the weekly summary settings"))); }, [load]);

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const out = await fn();
      if (okMsg) toast.success(okMsg);
      return out;
    } catch (e) {
      toast.error(errText(e, "Something went wrong"));
      return null;
    } finally {
      setBusy("");
    }
  };

  const toggleMaster = (enabled) => act("master", async () => {
    await api.patch("/admin/buyer-weekly-summary", { enabled });
    setPreview(null);
    await load();
  }, enabled ? "Weekly summary turned on" : "Weekly summary turned off");

  const toggleBuyer = (b, enabled) => act(b.page_id, async () => {
    await api.patch(`/admin/buyer-weekly-summary/buyers/${b.page_id}`, { enabled });
    setPreview(null);
    await load();
  }, `${b.firm_name}: weekly summary ${enabled ? "on" : "off"}`);

  const runPreview = () => act("preview", async () => {
    const r = await api.post("/admin/buyer-weekly-summary/preview");
    setPreview(r.data);
  });

  const sendNow = () => {
    if (!window.confirm("Send last week's summary to every enabled buyer now? Buyers who already got this week's email are skipped.")) return;
    act("send", async () => {
      await api.post("/admin/buyer-weekly-summary/send-now");
      setTimeout(() => { load().catch(() => {}); }, 5000);
    }, "Sending now — the Last run line updates in a few seconds");
  };

  if (loadError) {
    return (
      <div className="border-t border-black p-4" data-testid="buyer-weekly-summary">
        <div className="label-mono mb-2">Weekly buyer summary email</div>
        <div className="border-2 border-red-700 bg-red-50 p-3 text-xs" data-testid="buyer-weekly-summary-error">{loadError}</div>
      </div>
    );
  }
  if (!data) return null;
  return (
    <div className="border-t border-black p-4" data-testid="buyer-weekly-summary">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <EnvelopeSimple size={14} weight="bold" />
        <div className="label-mono">Weekly buyer summary email</div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest" data-testid="buyer-weekly-summary-master-label">
            {data.enabled ? "On for all buyers" : "Off for all buyers"}
          </span>
          <Switch checked={data.enabled} disabled={!canEdit || busy === "master"} onCheckedChange={toggleMaster}
                  data-testid="buyer-weekly-summary-master" />
        </div>
      </div>
      <div className="text-[10.5px] text-zinc-600 mb-1">
        Sent {data.schedule}. Buyers with no visits or leads that week are skipped. Use the switches below to leave out a buyer.
      </div>
      <div className="text-[10.5px] text-zinc-600 mb-3" data-testid="buyer-weekly-summary-last-run"><LastRun run={data.last_run} /></div>
      <BuyerToggles buyers={data.buyers} masterOn={data.enabled} onToggle={toggleBuyer} busy={busy} canEdit={canEdit} />
      {!canEdit && (
        <div className="text-[10.5px] text-zinc-500 mt-2" data-testid="buyer-weekly-summary-readonly">
          Only Super Admins and Admins can change these settings.
        </div>
      )}
      <div className="flex gap-2 mt-3 flex-wrap">
        <button type="button" className={BTN} disabled={busy === "preview"} onClick={runPreview} data-testid="buyer-weekly-summary-preview-btn">
          {busy === "preview" ? "Checking…" : "Preview last week"}
        </button>
        <button type="button" className={BTN} disabled={!canEdit || !data.enabled || busy === "send"} onClick={sendNow}
                title={data.enabled ? "" : "Turn the weekly summary on first"} data-testid="buyer-weekly-summary-send-now">
          Send last week&apos;s summary now
        </button>
      </div>
      {preview && <div className="mt-3"><PreviewTable preview={preview} /></div>}
    </div>
  );
};
